// @flow

import * as React from 'react';

import decodeString from '@ta/common.decode';
import { featureIsEnabled } from '@ta/common.features';
import {
  BoostAction,
  DisabledSocialAction,
  LikeAction,
  NestedSocialAction,
  SaveAction,
  SocialActionBar,
} from '@ta/social.actions';
import { ShareCta } from '@ta/public.share-cta';
import type { AuthZInfo } from '@ta/common.authz';

import LinkPostSection from '@ta/social.section-link-post';
import type { LinkPostSectionProps } from '@ta/social.section-link-post';
import SocialStatisticsBar from '@ta/social.statistics-bar';
import { getDisplayName } from '@ta/social.member';

type Props = {
  ...$Diff<LinkPostSectionProps, {| socialActionBar: ?React.Node |}>,
  loggedInMemberAuthorization?: AuthZInfo,
};

const LinkPostSectionWrap = (props: Props) => {
  const { loggedInMemberAuthorization, ...copiedProps } = props;
  const { post, member, actor } = copiedProps;

  if (!post || !post.object) {
    return null;
  }

  const boostAction =
    featureIsEnabled('social_boosting_enabled') &&
    (loggedInMemberAuthorization && loggedInMemberAuthorization.canBoostContent) ? (
      <BoostAction
        socialObjectRef={{ type: 'LINK_POST', id: post.object.postId }}
        socialStatistics={post.object.socialStatistics}
        typename={post.object.__typename}
      />
    ) : null;

  const repostProps = {
    modalName: `repost_${post.object.__typename}_${post.object.postId}`,
    repostPreviewProps: { feedSectionItem: post, member, actor },
    actor,
    socialStatistics: post.object.socialStatistics,
  };

  const socialStatisticsBar = (
    <SocialStatisticsBar
      stats={post.object.socialStatistics}
      socialRef={{
        type: post.object.__typename,
        id: `${post.object.postId}`,
      }}
      socialProofParams={{ displayName: getDisplayName(actor) }}
      deduplicate
    />
  );

  const socialActionBar = (
    <SocialActionBar>
      <NestedSocialAction padRight>
        {/* TRVX-13661: Likes -> Helpful Tech Debt */}
        <LikeAction
          item={post.object}
          socialObject={{ objectType: 'LINK_POST', objectId: post.object.postId }}
        />
        {featureIsEnabled('trips_save_ugc_links') && (
          <SaveAction object={post.object} displayStyle="icon+text" />
        )}
      </NestedSocialAction>
      <NestedSocialAction>
        {/* Share: TRVX-9358 */}
        {featureIsEnabled('social_ugc_share') ? (
          <ShareCta
            url={`${decodeString(post.object.preview.canonicalUrl)}`}
            itemType="LinkPost"
            itemId={post.object.postId}
            styleAsSocialAction
            alignEdge="right"
            repostProps={repostProps}
          />
        ) : (
          <DisabledSocialAction actionType="Share" />
        )}
        {boostAction}
      </NestedSocialAction>
    </SocialActionBar>
  );

  return (
    <LinkPostSection
      {...copiedProps}
      socialStatisticsBar={socialStatisticsBar}
      socialActionBar={socialActionBar}
    />
  );
};

LinkPostSectionWrap.defaultProps = {
  isSingleObject: false,
  isEmbedded: false,
  member: null,
  actor: null,
  deleteObject: null,
  untagObject: null,
  loggedInUserId: '',
  clusterId: undefined,
};

export default LinkPostSectionWrap;
